import {useEffect, useRef} from 'react'
import {useTranslation} from 'react-i18next'

import {useMapLayersV2} from '../../../../../../Map/MapLayersV2Context'
import {useMapLegendContext} from '../../../Helpers/MapLegend'
import {getSoilMapLegendModel, selectSoilMapOfficialLinks} from './soilMapVisuals'

const formatScale = (denominator) => {
  const value = Number(denominator)

  if (!Number.isFinite(value) || value <= 0) {
    return null
  }

  return `1:${new Intl.NumberFormat('es-AR', {maximumFractionDigits: 0}).format(value)}`
}

const parseStructuredValue = (value, fallback) => {
  if (value == null || value === '') {
    return fallback
  }

  if (typeof value !== 'string') {
    return value
  }

  try {
    return JSON.parse(value)
  } catch (error) {
    return fallback
  }
}

const SoilMap = ({land, soilMapData}) => {
  const {t} = useTranslation()
  const mapLayers = useMapLayersV2()
  const mapLegendContext = useMapLegendContext()
  const mapLayersRef = useRef(mapLayers)
  const mapLegendContextRef = useRef(mapLegendContext)
  const translationRef = useRef(t)
  const layerId = `soil-map-${land.land_id}`

  mapLayersRef.current = mapLayers
  mapLegendContextRef.current = mapLegendContext
  translationRef.current = t

  useEffect(() => {
    if (
      soilMapData?.status !== 'available' ||
      soilMapData?.featureCollection?.type !== 'FeatureCollection'
    ) {
      return undefined
    }

    mapLayersRef.current.land.soilMap.add({
      landId: land.land_id,
      geojson: soilMapData.featureCollection,
      layerId,
      opacity: 0.86,
      fit: true,
      getPopupContent: (properties) => {
        const translate = translationRef.current
        const components = parseStructuredValue(properties.soilComponents, [])
        const usefulWater = parseStructuredValue(properties.usefulWaterStorage, {})
        const unitDocument = parseStructuredValue(properties.unitDocument, {})
        const limitations = parseStructuredValue(properties.capabilityLimitationCodes, [])
        const memory = soilMapData?.documentation?.memory || {}
        const limitationLabel = limitations
          .map((code) =>
            translate(`images:soil_map.limitation_${String(code).toLowerCase()}`)
          )
          .filter(Boolean)
          .join(', ')
        const componentRows = components.map((component) => ({
          label: component.name,
          value: [
            component.percentage != null ? `${component.percentage} %` : null,
            component.phase,
          ]
            .filter(Boolean)
            .join(' · '),
        }))
        const componentLinks = components.map((component) => ({
          label: translate('images:soil_map.series_document', {series: component.name}),
          href: component.documentUrl,
        }))
        const contextualLinks = [
          {
            label: translate('images:soil_map.unit_document'),
            href: unitDocument.url,
          },
          ...componentLinks,
        ]
        const fallbackUrl = memory.url || properties.memoryUrl
        const fallbackLink = fallbackUrl
          ? {
              label: translate('images:soil_map.memory_document', {
                name: memory.title || translate('images:soil_map.memory'),
              }),
              href: fallbackUrl,
            }
          : {
              label: translate('images:soil_map.official_source'),
              href: soilMapData?.documentation?.sourcePageUrl,
            }

        return {
          title: properties.soilUnit || translate('images:soil_map.unknown_unit'),
          rows: [
            {
              label: translate('images:soil_map.capability'),
              value: properties.capabilityClass,
            },
            {
              label: translate('images:soil_map.limitations'),
              value: limitationLabel,
            },
            {
              label: translate('images:soil_map.productivity'),
              value: properties.productivityIndex,
            },
            {
              label: translate('images:soil_map.unit_type'),
              value: properties.cartographicUnitType,
            },
            {
              label: translate('images:soil_map.scale'),
              value: formatScale(properties.scaleDenominator),
            },
          ],
          sections: [
            {
              title: translate('images:soil_map.description'),
              text: properties.description,
            },
            {
              title: translate('images:soil_map.composition'),
              rows: componentRows,
            },
            {
              title: translate('images:soil_map.useful_water'),
              rows: [
                {
                  label: translate('images:soil_map.depth_1m'),
                  value:
                    usefulWater.depth1m != null ? `${usefulWater.depth1m} mm` : null,
                },
                {
                  label: translate('images:soil_map.depth_1_5m'),
                  value:
                    usefulWater.depth1_5m != null ? `${usefulWater.depth1_5m} mm` : null,
                },
                {
                  label: translate('images:soil_map.depth_2m'),
                  value:
                    usefulWater.depth2m != null ? `${usefulWater.depth2m} mm` : null,
                },
              ],
            },
          ],
          linksTitle: translate('images:soil_map.official_information'),
          links: selectSoilMapOfficialLinks({
            contextualLinks,
            fallbackLink,
            viewerLink: {
              label: translate('images:soil_map.official_viewer'),
              href: soilMapData?.documentation?.mapViewerUrl,
            },
          }),
        }
      },
    })
    mapLegendContextRef.current.changeBasicLegend(
      land.land_id,
      getSoilMapLegendModel(soilMapData)
    )

    return () => {
      mapLayersRef.current.land.soilMap.remove({
        landId: land.land_id,
        layerId,
      })
      mapLegendContextRef.current.clearBasicLegend(land.land_id)
    }
  }, [land.land_id, layerId, soilMapData])

  return null
}

export default SoilMap
